import React, { useState } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeftIcon, PhoneIcon, ChevronLeftIcon, ChevronRightIcon, ArrowDownIcon, AlertCircleIcon, XIcon } from 'lucide-react';
interface RichBlock {
  text: string;
  style?: 'normal' | 'italic-bold' | 'red-italic-bold' | 'bold';
}
interface NumberedItem {
  type: 'numbered-list';
  items: string[];
  style?: 'italic-bold';
}
type ContentBlock = string | RichBlock | NumberedItem;
interface ContentPage {
  type: 'reading';
  heading: string;
  paragraphs: string[];
  richContent?: ContentBlock[];
  bulletPoints?: {
    term: string;
    description: string;
  }[];
}
interface QuestionPage {
  type: 'question';
  question: string;
  correctAnswer: string;
  options: {
    label: string;
    value: string;
  }[];
}
type ModulePage = ContentPage | QuestionPage;
interface Module {
  id: number;
  title: string;
  pages: ModulePage[];
}
const modules: Module[] = [{
  id: 1,
  title: 'Module 1',
  pages: [{
    type: 'reading',
    heading: 'Section 1 Who Represents Whom?',
    paragraphs: ['A typical real estate transaction in Arizona involves two real estate licensees; one that is working with and represents the buyer, and the other that is working with and represents the seller. Regardless of the locale, size of the real estate community and number of licensees, that type of transaction probably occurs 80% of the time.', 'The remaining 20% could be variations whereby the seller might sell direct to the buyer without any licensees involved, or a listing agent that represents a seller when selling the property to a buyer, yet the buyer has no representation or dual agency when there are two agents under the same broker, or when the listing agent works with a buyer yet represents both the buyer and seller.', 'In the typical transaction, agency disclosure is often the most overlooked or confusing part of the obligations of the licensees. A poor agency disclosure often leads to confusion on the part of the clients. If the buyer or seller does not know who, or if anyone, represents them, that could lead to issues in the future. A confused client, for any reason, is likely to pose that question to an attorney, which may result in a legal claim.', 'Residential or commercial transactions? With over 70,000 active real estate licensees in the state, most of them are residential agents using standard, state-wide forms. Many of the forms provide a sufficient agency disclosure. Yet even so, as easy as it appears to be for a residential licensee to make a valid agency disclosure, many licensees fail to do so.']
  }, {
    type: 'question',
    question: 'Would a broker involved in a commercial lease have the same agency disclosure obligation as a salesperson selling their residential listing to a buyer?',
    correctAnswer: 'yes',
    options: [{
      label: 'Yes',
      value: 'yes'
    }, {
      label: 'No',
      value: 'no'
    }]
  }]
}, {
  id: 2,
  title: 'Module 2',
  pages: [{
    type: 'reading',
    heading: '',
    paragraphs: ['Yes, commercial licensees, and those that offer real estate services such as leasing, industrial sales, land, commercial and other types of real estate transactions are often remiss in making full and adequate agency disclosures. However, there are fewer standardized forms for those licensees to use.', 'In addition, many licensees that provide commercial and other non-residential services are often misinformed as to their obligation to provide a full and adequate agency disclosure. They and/or their broker have the belief that the rules and statutes apply to residential licensees only and not to the transactions that they create as commercial or industrial brokers.', 'It therefore comes as a surprise that when challenged, the rules and statutes refer to "licensees, brokers and salespersons" without any specific activity such as residential. Thus, brokers that offer services in commercial sales and leasing, land, industrial, time-share and builder/developer sales have the same obligation to make a full agency disclosure as a licensee that sells houses.', 'Standardized Forms; in Arizona, the Arizona Association of Realtors (AAR) offers standardized forms to the 46,000+ Realtors in the state. There are well over 80 different forms, including those that provide the required agency disclosure to the client and a section or sections allowing the client to elect how they will be represented. In addition, the newly formed Non-Member MLS Access forms (will be explained later) provide similar disclosure through forms that the clients would review and execute. That would include'],
    bulletPoints: [{
      term: 'The Real Estate Agency',
      description: 'form. This is the more commonly used form which allows the Realtor member to disclose the various types of representation they might provide to the client, and check boxes allowing the client to "elect" the method of representation they would prefer.'
    }, {
      term: 'Buyer-Broker Agreement.',
      description: 'This form is used when the buyer elects to employ the licensee exclusively. We will review the use of this form later in this class.'
    }, {
      term: 'Dual Agency Disclosure form.',
      description: "One of the more misunderstood forms. This is used when the transaction between the seller and buyer invokes dual agency either because both the buyer's agent and the listing agent work for the same broker, or the listing agent disclosed to the buyer that they were acting as a dual agent, and the buyer agreed. We will review dual agency in Section 2 of this class."
    }, {
      term: 'Disclosure of Buyer Agency and Seller Waiver and Confirmation.',
      description: 'Another misunderstood form that the real estate community is slow to embrace. This form should be used when the listing broker is acting as a "limited services" broker. Limited services brokers are those that will engage with the seller and provide services such as MLS entry, signage and a lockbox, yet the broker will not be involved in the day-to-day showing of the property nor will the broker participate in any contract presentation and/or negotiation.'
    }]
  }, {
    type: 'question',
    question: 'Does a limited services listing broker allow the buyers agent to present a contract direct to the seller without the listing broker present?',
    correctAnswer: 'yes',
    options: [{
      label: 'Yes',
      value: 'yes'
    }, {
      label: 'No',
      value: 'no'
    }]
  }]
}, {
  id: 3,
  title: 'Module 3',
  pages: [{
    type: 'reading',
    heading: '',
    paragraphs: [],
    richContent: ["Yes, a limited services broker allows the buyer's agent to work directly with the seller without the listing broker present or involved. This was a business model that gained popularity in the early 2000s and was offered as part of a flat-rate or discount broker. As the market heated up in the early 2000's, many sellers felt the urgency to sell a property yet felt that they could generate a buyer and close a deal without a licensee. While that was somewhat possible, those sellers still needed MLS exposure.", 'The limited services concept was developed as a means of providing the essential basic marketing exposure through the MLS, yet not much more than that.', "As the practice gained ground, it soon became apparent that the buyer's agents were reluctant to work directly with the seller, even though the listing agent offered permission in the listing. This was because of Requirements for Licensees R4-28-1102, which reads as follows;", {
      text: "Except for owner listed properties, negotiations shall be conducted exclusively through the principal's broker or the broker's representative unless:",
      style: 'italic-bold'
    }, {
      type: 'numbered-list',
      items: ['The principal waives this requirement in writing, and', 'No licensed representative of the broker is available for 24 hours.'],
      style: 'italic-bold'
    }, 'That wording was revised effective December 13, 2025, and now R4-28-1102 reads', {
      text: "Real estate licensees may not contact a principal represented by another licensee unless the principal's Designated Broker, broker representative with delegated authority, and the licensee are unavailable for 24 hours. A Principal may waive or alter this requirement by issuing written instructions.",
      style: 'red-italic-bold'
    }, 'Also, the new rule added R4-28-1102.B as follows;', {
      text: "For a buyer and unless the buyer waives this requirement in writing, negotiations must be conducted exclusively through the principal's broker or the broker's representative unless the designated broker, a broker with delegated authority from the designated broker, and the buyer's licensee with an agency relationship are unavailable for 24 hours.",
      style: 'red-italic-bold'
    }]
  }, {
    type: 'question',
    question: 'If a seller is represented by a limited services broker, should the buyers agent disclose that fact to their buyer?',
    correctAnswer: 'yes',
    options: [{
      label: 'Yes',
      value: 'yes'
    }, {
      label: 'No',
      value: 'no'
    }]
  }]
}, {
  id: 4,
  title: 'Module 4',
  pages: [{
    type: 'reading',
    heading: '',
    paragraphs: [],
    richContent: ["Yes, when a licensee is showing a listing to their buyer, and that property is listed by a limited services broker, that fact should be disclosed to the buyer. While the situation would typically have little or no impact on the buyer, there might be some unique situations that could affect the transaction. The form itself does not require the buyers signature, but that does not lessen the need to make the disclosure to the buyer. The buyer's licensee would be made aware that the property was listed by a limited services broker as a result of the disclosure of that fact in the MLS.", {
      text: 'Timing of an agency disclosure and election. As licensees working at a real estate brokerage, we should find ourselves operating under a body of rules and obligations that are imposed by the state regulatory agency (ADRE), our local and state association (assuming we are members) and by our broker. Such rules, which are broad in their scope and include our agency disclosure obligations would include;',
      style: 'normal'
    }, {
      text: '1.  Arizona Requirements for Licensees, found in the Arizona Administrative Code, in Title 4, Chapter 28. All the regulatory agencies in Arizona have rules that the licensees must follow. These rules would contain some language regarding how we represent clients, how we disclose that representation and how the client might be allowed to make a choice or election of how we represent them. One such Rule is R4-28-1101.F reads;',
      style: 'normal'
    }, {
      text: 'A salesperson or broker shall not accept compensation from or represent more than one party to a transaction without the prior written consent of all parties.',
      style: 'italic-bold'
    }, "This part of the Requirements for Licensees dictates our obligation to obtain prior written consent to act as a dual agent in a transaction. We will review this in more detail later in the class. Thus, the Requirements for Licensees not only tells us we must obtain a client's consent to act as a dual agency, it tells us when we must obtain that consent.", {
      text: '2.  Arizona Revised Statutes. The real estate commissioner is empowered to regulate our real estate licenses because of statutes contained within the Arizona Revised Statutes (ARS) in Arizona. We will review some of those statutes later in the class.',
      style: 'normal'
    }, 'One statute, ARS 32-2153 describes the circumstances under which the Real Estate Commissioner could suspend or revoke a real estate license. While not a complete list, the statute has various obligations for real estate licensees specifically #2 which obligates the licensees to obtain \u201Cprior written consent\u201D before representing both parties to a transaction.']
  }, {
    type: 'question',
    question: 'Only Realtor members are obligated to adhere to the Realtor Code of Ethics?',
    correctAnswer: 'yes',
    options: [{
      label: 'Yes',
      value: 'yes'
    }, {
      label: 'No',
      value: 'no'
    }]
  }]
}, {
  id: 5,
  title: 'Module 5',
  pages: [{
    type: 'reading',
    heading: '',
    paragraphs: [],
    richContent: ['Yes, in most cases. The Code of Ethics is a body of rules for the National Association of Realtors (NAR). Of the 74,000+ licensees in Arizona, about 61,000 of them are active. The remaining licensees do not have a direct obligation to follow the rules of an association of which they are not a subscribing member. But many of the parts of the Code are duplicated in the\nRequirements for Licensees and Arizona Statutes, which all licensees must follow.', {
      text: '3.  Realtor Code of Ethics. Any Realtor licensee is obligated to follow the Code of Ethics as part of their obligations under the Realtor name. There are several areas of the Code of Ethics that deal with our obligation as a buyer\u2019s agent, a seller\u2019s agent or as a dual agent. Some of those are;',
      style: 'normal'
    }]
  }, {
    type: 'question',
    question: 'The Realtor Code of Ethics must be followed by all real estate licensees?',
    correctAnswer: 'no',
    options: [{
      label: 'Yes',
      value: 'yes'
    }, {
      label: 'No',
      value: 'no'
    }]
  }]
}, {
  id: 6,
  title: 'Module 6',
  pages: [{
    type: 'reading',
    heading: '',
    paragraphs: [],
    richContent: ['No, while a good business practice, the Realtor Code of Ethics is meant for the 1.3 million Realtors in the US, and approximately 48,000 in Arizona. While many parts of the Code are duplicated in the Requirements for Licensees and statute, the only persons that can be held accountable are Realtor members.', {
      text: 'Article 1, Standard of Practice 1-5 states,',
      style: 'normal'
    }, {
      text: '\u201CRealtors may represent the seller/landlord and buyer/tenant in the same transaction only after full disclosure to and with informed consent of both parties.\u201D (Mirrors the Requirements for Licensees R4-28-1101.F)',
      style: 'italic-bold'
    }, {
      text: 'Article 1, Standard of Practice 1-12 states,',
      style: 'normal'
    }, {
      text: '\u201CWhen entering into listing contracts, REALTORS\u00AE must advise sellers/landlords of any potential for listing brokers to act as disclosed dual agents, e.g. buyer/tenant agents. (Use of the READE form accomplishes this obligation)',
      style: 'italic-bold'
    }, {
      text: 'Article 1, Standard of Practice 1-13 states,',
      style: 'normal'
    }, {
      text: '\u201CWhen entering into buyer/tenant agreements, REALTORS\u00AE must advise potential clients of any potential for the buyer/tenant representative to act as a disclosed dual agent, e.g. listing broker, sub agent, landlord\u2019s agent, etc.\u201D',
      style: 'italic-bold'
    }, {
      text: 'Article 16, Standard of Practice 16-9 states,',
      style: 'normal'
    }, {
      text: '\u201CRealtors, prior to entering into a representation agreement, have an affirmative obligation to make reasonable efforts to determine whether the prospect is subject to a current, valid exclusive agreement to provide the same type of real estate service.\u201D',
      style: 'italic-bold'
    }, 'Article 16-9 was previously worded to require agents only to determine if a seller was bound to an exclusive listing. It was revised several years ago, to be more generic, meaning it is meant to require an agent to determine if the client is bound to ANY exclusive agreement, which can mean a listing, but could also mean a Buyer/Broker agreement.', 'Article 16, Standard of Practice 16-9 of the Code of Ethics is the most-often forgotten issue. Let\u2019s say you are sitting in an open house, or a prospect calls you on your sign, ad, Internet posting, etc. He wants information and you are prepared to provide it. But before you start working with that buyer, you are obligated to determine if he is bound to any current, valid exclusive agreement. That needs to be a question you ask EVERY potential buyer, since failing to do so could mean a violation of the Code of Ethics.', 'While many of us were trained in real estate school and/or by our broker to ask', {
      text: '\u201Care you working with another agent...\u201D',
      style: 'italic-bold'
    }, 'that question is insufficient, in that the answer the buyer or seller might provide is insufficient to assist you in complying with the Code of Ethics. The correct question is', {
      text: '\u201Chave you signed an exclusive agreement with another broker...\u201D',
      style: 'italic-bold'
    }, 'and explore that more if they say yes, to determine if they actually signed an exclusive agreement, and not just the READE (Real Estate Agency Disclosure and Election) form.']
  }]
}];
const quizQuestions = [{
  question: 'A Limited Services Broker generally means',
  correctAnswer: 'b',
  options: [{
    letter: 'A',
    text: 'The listing broker will not represent anyone in the transaction'
  }, {
    letter: 'B',
    text: 'The listing broker will provide limited services, yet will still represent the seller'
  }, {
    letter: 'C',
    text: 'The listing broker will provide MLS access and signage yet will not represent the seller'
  }, {
    letter: 'D',
    text: "The listing broker will transfer the representation to the buyer's agent, thus making the buyer's agent a dual agent"
  }]
}, {
  question: 'Which of the following is most true about the Requirements for Licensees and Arizona Statutes?',
  correctAnswer: 'c',
  options: [{
    letter: 'A',
    text: 'The rules and statutes deal only with residential agents and do not apply to those selling commercial real estate'
  }, {
    letter: 'B',
    text: 'The rules and statutes specifically state that they apply to all licensees'
  }, {
    letter: 'C',
    text: 'The rules and statutes refer to licensees and are not specific to residential or commercial, thus apply to all licensees'
  }, {
    letter: 'D',
    text: 'The rules and statutes specifically exempt any licensee not selling residential real estate'
  }]
}, {
  question: 'The Code of Ethics of the National Association of Realtors',
  correctAnswer: 'd',
  options: [{
    letter: 'A',
    text: 'Must be following by all Realtor members'
  }, {
    letter: 'B',
    text: 'Are often duplicated in state rule and/or statute'
  }, {
    letter: 'C',
    text: 'Demonstrate good business practices that non-members should follow'
  }, {
    letter: 'D',
    text: 'All of the above'
  }]
}, {
  question: 'Part of the Requirements for Licensees in R4-28-1101.F that states the licensee should obtain "prior written consent" is addressing',
  correctAnswer: 'c',
  options: [{
    letter: 'A',
    text: "The licensee's obligation to disclose that they have a real estate license"
  }, {
    letter: 'B',
    text: "The licensee's obligation to disclose the compensation that they are earning"
  }, {
    letter: 'C',
    text: "The licensee's obligation to obtain permission to be a dual agent"
  }, {
    letter: 'D',
    text: 'All of the above'
  }]
}, {
  question: 'The AAR form titled "Disclosure of Buyer Agency and Seller Waiver and Confirmation" is used',
  correctAnswer: 'a',
  options: [{
    letter: 'A',
    text: 'When the listing broker is providing limited services yet still represents the seller'
  }, {
    letter: 'B',
    text: 'When the buyer is not represented'
  }, {
    letter: 'C',
    text: 'When the seller is not represented'
  }, {
    letter: 'D',
    text: 'All of the above'
  }]
}];
export function ClassContent() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialSection = parseInt(searchParams.get('section') || '1', 10);
  const [activeModule, setActiveModule] = useState(isNaN(initialSection) ? 1 : initialSection);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [sectionReview, setSectionReview] = useState('');
  const [selectedAnswer, setSelectedAnswer] = useState('');
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizAnswers, setQuizAnswers] = useState<Record<number, string>>({});
  const [quizSubmitted, setQuizSubmitted] = useState(false);
  const [quizShowFeedback, setQuizShowFeedback] = useState(false);
  const [quizSectionReview, setQuizSectionReview] = useState('');
  const [quizModal, setQuizModal] = useState<{
    type: 'unanswered' | 'incorrect' | null;
    items: number[];
  }>({
    type: null,
    items: []
  });
  const [showWrongAnswerModal, setShowWrongAnswerModal] = useState(false);
  const displayName = slug?.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || 'Class';
  const currentModule = modules.find(m => m.id === activeModule);
  const totalPages = currentModule?.pages.length || 0;
  const currentPage = currentModule?.pages[currentPageIndex];
  const handleModuleChange = (modId: number) => {
    setActiveModule(modId);
    setCurrentPageIndex(0);
    setSelectedAnswer('');
  };
  const handlePrev = () => {
    if (currentPageIndex > 0) {
      setCurrentPageIndex(prev => prev - 1);
      setSelectedAnswer('');
    } else if (activeModule > 1) {
      const prevMod = modules.find(m => m.id === activeModule - 1);
      setActiveModule(activeModule - 1);
      setCurrentPageIndex(prevMod ? Math.max(0, prevMod.pages.length - 1) : 0);
      setSelectedAnswer('');
    }
  };
  const handleNext = () => {
    if (isQuestionPage && !isCorrectAnswer) {
      setShowWrongAnswerModal(true);
      return;
    }
    if (isLastPage) {
      setShowQuiz(true);
      return;
    }
    if (currentPageIndex < totalPages - 1) {
      setCurrentPageIndex(prev => prev + 1);
      setSelectedAnswer('');
    } else if (activeModule < modules.length) {
      setActiveModule(activeModule + 1);
      setCurrentPageIndex(0);
      setSelectedAnswer('');
    }
  };
  const isFirstPage = activeModule === 1 && currentPageIndex === 0;
  const isLastPage = activeModule === modules.length && currentPageIndex >= totalPages - 1;
  const isQuestionPage = currentPage?.type === 'question';
  const isCorrectAnswer = isQuestionPage && selectedAnswer === (currentPage as QuestionPage).correctAnswer;
  const nextDisabled = isQuestionPage && !isCorrectAnswer;
  const handleQuizAnswer = (questionIndex: number, answer: string) => {
    if (quizSubmitted) return;
    // Clear visual feedback when user is correcting their answers
    if (quizShowFeedback) setQuizShowFeedback(false);
    setQuizAnswers(prev => ({
      ...prev,
      [questionIndex]: answer
    }));
  };
  const handleQuizSubmit = () => {
    const unanswered: number[] = [];
    quizQuestions.forEach((_, i) => {
      if (!quizAnswers[i]) unanswered.push(i + 1);
    });
    if (unanswered.length > 0) {
      setQuizModal({
        type: 'unanswered',
        items: unanswered
      });
      return;
    }
    const incorrect: number[] = [];
    quizQuestions.forEach((q, i) => {
      if (quizAnswers[i] !== q.correctAnswer) incorrect.push(i + 1);
    });
    if (incorrect.length > 0) {
      setQuizShowFeedback(true);
      setQuizModal({
        type: 'incorrect',
        items: incorrect
      });
      return;
    }
    setQuizShowFeedback(true);
    setQuizSubmitted(true);
  };
  const scrollToQuizQuestion = (qNum: number) => {
    setQuizModal({
      type: null,
      items: []
    });
    setTimeout(() => {
      const el = document.getElementById(`quiz-q-${qNum - 1}`);
      if (el) el.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }, 100);
  };
  const correctCount = quizQuestions.filter((q, i) => quizAnswers[i] === q.correctAnswer).length;
  return <div className="space-y-0 -m-4 lg:-m-8" data-id="element-1780" data-mp="1z9uzk1i6e3vq">
      {/* Top Header Bar */}
      <div className="bg-white border-b border-slate-200 px-4 lg:px-6 py-3" data-id="element-1781" data-mp="241wg3zmdmgrk">
        <div className="flex items-center justify-between max-w-7xl mx-auto" data-id="element-1782" data-mp="1q9ye08r31l4w">
          <div className="flex items-center gap-4" data-id="element-1783" data-mp="1q7vk3dmt77uw">
            <Link to={`/student/class/${slug}`} className="inline-flex items-center gap-1.5 text-xs text-purple-700 hover:text-purple-900 transition-colors" data-id="element-1784" data-mp="1va4e8dbf4fyl">
              <ArrowLeftIcon className="h-3.5 w-3.5" data-id="element-1785" data-mp="1l9txiiq6cqeo" />
              Back
            </Link>
            <div className="flex items-center gap-2 text-xs text-slate-500" data-id="element-1786" data-mp="11oboezjrb6de">
              <PhoneIcon className="h-3 w-3" data-id="element-1787" data-mp="xnrqvuv9u0qq" />
              <span data-id="element-1788" data-mp="xp2spp55oyf7">For Class Support: Call or text: 602-377-6207</span>
            </div>
          </div>
          <div className="flex items-center gap-3" data-id="element-1789" data-mp="1boube36mb2zj">
            {/* Scroll notice */}
            <div className="hidden md:flex items-center gap-1.5 bg-green-100 border border-green-300 text-green-800 text-xs font-bold px-3 py-2 rounded-lg" data-id="element-1790" data-mp="16nev0ofdxknh">
              <ArrowDownIcon className="h-3.5 w-3.5" data-id="element-1791" data-mp="1gmm3ax79ip3q" />
              Scroll to bottom to proceed.
            </div>
            {/* Leave Class button */}
            <Link to={`/student/class/${slug}`} className="inline-flex items-center px-3 py-1.5 text-xs font-bold text-white rounded transition-colors hover:opacity-90" style={{
            backgroundColor: '#CC0000'
          }} data-id="element-1792" data-mp="1uczct3zsybv6">
              Leave/Pause Class
            </Link>
          </div>
        </div>
      </div>

      {/* Course Title */}
      <div className="text-center py-6 bg-white border-b border-slate-200" data-id="element-1793" data-mp="k2lznyuaz5m8">
        <h1 className="text-2xl font-bold text-slate-900 mb-1" data-id="element-1794" data-mp="oy1bymcnwtgj">
          {displayName}
        </h1>
        <p className="text-sm font-semibold" style={{
        color: '#4A2D8F'
      }} data-id="element-1795" data-mp="tz8jckaw7qmo">
          Section 1
        </p>
      </div>

      {showQuiz /* Quiz View */ ? <div className="max-w-4xl mx-auto px-4 lg:px-6 py-6" data-id="element-1796" data-mp="1buul9hcm1dn">
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden p-6" data-id="element-1797" data-mp="68bgdx12s3nk">
            <h2 className="text-center text-lg font-bold text-slate-900 mb-5" data-id="element-1798" data-mp="b76bnyg8bkok">
              {displayName}
            </h2>

            {/* Performance Box */}
            <div className="border border-purple-200 bg-purple-50/50 rounded-lg p-5 mb-6 text-sm text-slate-800 shadow-sm" data-id="element-1799" data-mp="3eoxx0tzv16ds">
              <p className="font-bold text-slate-900 mb-3 text-base" data-id="element-1800" data-mp="3jn7ujb924j2k">
                Your Performance
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-1801" data-mp="20rlk2uquep7i">
                <div className="bg-white p-3 rounded border border-purple-100" data-id="element-1802" data-mp="1wqrwy3l85efo">
                  <p className="text-slate-500 text-xs uppercase tracking-wider font-semibold mb-1" data-id="element-1803" data-mp="1rs873s3ifii1">
                    Total Questions
                  </p>
                  <p className="text-xl font-bold text-slate-900" data-id="element-1804" data-mp="1mslsbe819u74">
                    {quizQuestions.length}
                  </p>
                </div>
                <div className="bg-white p-3 rounded border border-purple-100" data-id="element-1805" data-mp="1d2sjgkz9zt6k">
                  <p className="text-slate-500 text-xs uppercase tracking-wider font-semibold mb-1" data-id="element-1806" data-mp="191qinzppn92h">
                    Correct Answers
                  </p>
                  <p className="text-xl font-bold text-slate-900" data-id="element-1807" data-mp="144740yp3oua0">
                    {quizSubmitted ? correctCount : 0}
                  </p>
                </div>
                <div className="bg-white p-3 rounded border border-purple-100" data-id="element-1808" data-mp="u3y71inab4tk">
                  <p className="text-slate-500 text-xs uppercase tracking-wider font-semibold mb-1" data-id="element-1809" data-mp="3zwd30rzmrxy">
                    Performance
                  </p>
                  <p className="text-xl font-bold text-slate-900" data-id="element-1810" data-mp="3v5xq3evijvm8">
                    {quizSubmitted ? Math.round(correctCount / quizQuestions.length * 100) : 0}
                    %
                  </p>
                </div>
              </div>
            </div>

            <p className="text-sm font-bold text-slate-900 mb-4" data-id="element-1811" data-mp="3ghtaijrrc07o">
              The quiz requires a 100% pass rate. Good luck!
            </p>

            {/* Section Review */}
            <div className="mb-5" data-id="element-1812" data-mp="3q84koo21b5n0">
              <p className="text-sm text-slate-600 mb-1" data-id="element-1813" data-mp="3q97fh72h9s87">Section Review</p>
              <select value={quizSectionReview} onChange={e => {
            const val = e.target.value;
            setQuizSectionReview(val);
            if (val === 'final-exam') {
              navigate(`/student/class/${slug}/final-exam`);
              return;
            }
            if (val) {
              const modId = parseInt(val, 10);
              setShowQuiz(false);
              setActiveModule(modId);
              setCurrentPageIndex(0);
              setSelectedAnswer('');
              setQuizSectionReview('');
            }
          }} className="border border-slate-300 rounded px-2 py-1 text-xs text-slate-700 bg-white" data-id="element-1814" data-mp="3lfbio7xyabem">
                <option value="" data-id="element-1815" data-mp="1vypqu7xwcgwa">Select One</option>
                <option value="1" data-id="element-1816" data-mp="1i098fjqo91yb">Section 1</option>
                <option value="2" data-id="element-1817" data-mp="1hzhqvctwt4r1">Section 2</option>
                <option value="3" data-id="element-1818" data-mp="1myb02t25y9le">Section 3</option>
                <option value="4" data-id="element-1819" data-mp="1n0xb52o9j78d">Section 4</option>
                <option value="5" data-id="element-1820" data-mp="6kb4fuacp63y">Section 5</option>
                <option value="6" data-id="element-1821" data-mp="6mnihwczmwbq">Section 6</option>
                <option value="final-exam" data-id="element-1822" data-mp="bkp3imtz55pu">Take The Final Exam</option>
              </select>
            </div>

            {/* Questions */}
            <div className="space-y-8" data-id="element-1823" data-mp="3np1ja96p4sa4">
              {quizQuestions.map((q, qi) => {
            const userAnswer = quizAnswers[qi];
            const isCorrect = userAnswer === q.correctAnswer;
            return <div key={qi} id={`quiz-q-${qi}`} className={`space-y-3 p-5 rounded-lg border transition-colors ${quizShowFeedback && !isCorrect ? 'bg-red-50 border-red-200' : quizShowFeedback && isCorrect ? 'bg-green-50 border-green-200' : 'bg-slate-50 border-slate-100'}`} data-id="element-1824" data-mp="39vsc8f680iur" data-mpi={qi}>
                    <div className="flex items-start gap-2" data-id="element-1825" data-mp="2vw14gw6aryp7">
                      <span className="flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full bg-purple-100 text-purple-700 text-xs font-bold mt-0.5" data-id="element-1826" data-mp="2qxg14nvr0ywj">
                        {qi + 1}
                      </span>
                      <p className="text-sm font-bold text-slate-900 leading-relaxed" data-id="element-1827" data-mp="p5um2ep1lzsz">
                        {q.question}
                      </p>
                      {quizShowFeedback && <span className={`flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-full ml-auto ${isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`} data-id="element-1828" data-mp="11mcfyf666emg">
                          {isCorrect ? '✓' : '✗'}
                        </span>}
                    </div>
                    <div className="space-y-2 pl-8" data-id="element-1829" data-mp="io9ozuu2a7m0">
                      {q.options.map(opt => <label key={opt.letter} className={`flex items-start gap-3 cursor-pointer text-sm p-3 rounded-md border transition-colors ${userAnswer === opt.letter.toLowerCase() ? 'bg-purple-50 border-purple-200' : 'bg-white border-slate-200 hover:border-purple-300'}`} data-id="element-1830" data-mp="nn30jhpsfojj">
                          <input type="radio" name={`quiz-q-${qi}`} value={opt.letter.toLowerCase()} checked={userAnswer === opt.letter.toLowerCase()} onChange={e => handleQuizAnswer(qi, e.target.value)} disabled={quizSubmitted} className="w-4 h-4 mt-0.5" style={{
                    accentColor: '#4A2D8F'
                  }} data-id="element-1831" data-mp="21nr2htxndpou" />
                          <span className="text-slate-700 leading-relaxed" data-id="element-1832" data-mp="2ubvbopffgkwa">
                            <strong className="text-slate-900" data-id="element-1833" data-mp="2ped00ra0zrkl">
                              {opt.letter}.
                            </strong>{' '}
                            {opt.text}
                          </span>
                        </label>)}
                    </div>
                  </div>;
          })}
            </div>

            {/* Submit / Actions */}
            <div className="mt-8 flex items-center gap-4 pt-6 border-t border-slate-200" data-id="element-1834" data-mp="3mxlcjti7ccst">
              <button onClick={handleQuizSubmit} disabled={quizSubmitted} className={`px-6 py-2.5 text-sm font-bold rounded-md transition-colors ${quizSubmitted ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'text-white hover:opacity-90 shadow-sm'}`} style={!quizSubmitted ? {
            backgroundColor: '#4A2D8F'
          } : {}} data-id="element-1835" data-mp="38y25l414n7i3">
                Submit Quiz
              </button>
              <button onClick={() => {
            setShowQuiz(false);
            setActiveModule(1);
            setCurrentPageIndex(0);
            setSelectedAnswer('');
          }} className="text-xs text-blue-600 hover:text-blue-800 underline" data-id="element-1836" data-mp="2g9whc7av70di">
                Section Review
              </button>
            </div>

            {quizSubmitted && <div className="mt-6 flex justify-end" data-id="element-1837" data-mp="i1sba1xfd04q">
                <button className="px-6 py-2.5 text-sm font-bold text-white rounded-md shadow-sm hover:opacity-90 transition-opacity" style={{
            backgroundColor: '#4A2D8F'
          }} data-id="element-1838" data-mp="d25who1y7btt">
                  Proceed to Next Section
                </button>
              </div>}
          </div>
        </div> /* Main Content Area */ : <div className="max-w-4xl mx-auto px-4 lg:px-6 py-6" data-id="element-1839" data-mp="vrc6mmooev40">
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-id="element-1840" data-mp="3lhpnegf5ci5p">
            {/* Section Header */}
            <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between flex-wrap gap-3 bg-slate-50/50" data-id="element-1841" data-mp="3gicwnkl5mqxu">
              <p className="text-sm text-slate-700 flex items-center gap-2" data-id="element-1842" data-mp="36git145igdil">
                <span className="w-2 h-2 rounded-full" style={{
              backgroundColor: '#4A2D8F'
            }} data-id="element-1843" data-mp="3bekaxlr7a6sh"></span>
                Section: <strong className="text-slate-900" data-id="element-1844" data-mp="3l4dk6g3ve9tk">Section 1</strong>
              </p>
              <div className="flex items-center gap-2 text-sm" data-id="element-1845" data-mp="3p45lq8uujfpf">
                <span className="text-slate-600" data-id="element-1846" data-mp="3u4a2lzzo2cg6">Section Review</span>
                <select value={sectionReview} onChange={e => {
              const val = e.target.value;
              setSectionReview(val);
              if (val === 'final-exam') {
                navigate(`/student/class/${slug}/final-exam`);
                return;
              }
              if (val) {
                const modId = parseInt(val, 10);
                setShowQuiz(false);
                setActiveModule(modId);
                setCurrentPageIndex(0);
                setSelectedAnswer('');
                setSectionReview('');
              }
            }} className="border border-slate-300 rounded px-2 py-1 text-xs text-slate-700 bg-white" data-id="element-1847" data-mp="3u2fketfs4yfs">
                  <option value="" data-id="element-1848" data-mp="3sn3dky6adu78">Select One</option>
                  <option value="1" data-id="element-1849" data-mp="3snmvievxsil9">Section 1</option>
                  <option value="2" data-id="element-1850" data-mp="3no9yj1bcpv4y">Section 2</option>
                  <option value="3" data-id="element-1851" data-mp="3npusaopuda1r">Section 3</option>
                  <option value="4" data-id="element-1852" data-mp="5iwxzdhkmjrg">Section 4</option>
                  <option value="5" data-id="element-1853" data-mp="5gaq1cqrq28p">Section 5</option>
                  <option value="final-exam" data-id="element-1854" data-mp="1gk2dhwjg28e">Take The Final Exam</option>
                </select>
              </div>
            </div>

            {/* Module Tabs */}
            <div className="px-6 pt-4 border-b border-slate-200" data-id="element-1855" data-mp="1cvz0wq4eb25m">
              <div className="flex gap-2 overflow-x-auto pb-[-2px]" data-id="element-1856" data-mp="1cvp8kvvvoiwr">
                {modules.map(mod => {
              const isActive = activeModule === mod.id;
              return <button key={mod.id} onClick={() => handleModuleChange(mod.id)} className={`
                        px-5 py-2.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-all rounded-t-md
                        ${isActive ? 'bg-purple-50/50 text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'}
                      `} style={isActive ? {
                borderColor: '#4A2D8F',
                color: '#4A2D8F'
              } : {}} data-id="element-1857" data-mp="1pfvzczhkl7m9">
                      {mod.title}
                    </button>;
            })}
              </div>
            </div>

            {/* Content */}
            <div className="p-6 md:p-8" data-id="element-1858" data-mp="3qm4s2l4rayk9">
              {currentPage?.type === 'reading' ? <div className="rounded-r-lg p-6 md:p-8 space-y-6 bg-slate-50 border-l-4 shadow-sm" style={{
            borderColor: '#4A2D8F'
          }} data-id="element-1859" data-mp="3ujj2gg46bcdu">
                  {currentPage.heading && <h3 className="font-bold text-sm text-slate-900" data-id="element-1860" data-mp="d5np7xx55ufb">
                      {currentPage.heading}
                    </h3>}
                  {currentPage.paragraphs.map((p, pi) => <p key={pi} className="text-sm text-slate-800 leading-relaxed" data-id="element-1861" data-mp="3d8790luh5uf" data-mpi={pi}>
                      {p}
                    </p>)}
                  {currentPage.richContent && currentPage.richContent.map((block, bi) => {
              if (typeof block === 'string') {
                return <p key={bi} className="text-sm text-slate-800 leading-relaxed" data-id="element-1862" data-mp="2gm26zr0we14k">
                            {block}
                          </p>;
              }
              if ('type' in block && block.type === 'numbered-list') {
                const nl = block as NumberedItem;
                return <ol key={bi} className="pl-12 space-y-1 list-decimal" data-id="element-1863" data-mp="1xor6k33j51nj">
                            {nl.items.map((item, ii) => <li key={ii} className={`text-sm leading-relaxed ${nl.style === 'italic-bold' ? 'font-bold italic text-slate-900' : 'text-slate-800'}`} data-id="element-1864" data-mp="26o4gsxzh9u9b" data-mpi={ii}>
                                {item}
                              </li>)}
                          </ol>;
              }
              const rb = block as RichBlock;
              const styleClasses = rb.style === 'italic-bold' ? 'font-bold italic text-slate-900' : rb.style === 'red-italic-bold' ? 'font-bold italic text-red-600' : rb.style === 'bold' ? 'font-bold text-slate-900' : 'text-slate-800';
              return <p key={bi} className={`text-sm leading-relaxed ${styleClasses}`} data-id="element-1865" data-mp="25yhdloqavivs" data-mpi={bi}>
                          {rb.text}
                        </p>;
            })}
                  {currentPage.bulletPoints && currentPage.bulletPoints.length > 0 && <div className="pl-6 space-y-4" data-id="element-1866" data-mp="1aq6rhw1xhmvn">
                        {currentPage.bulletPoints.map((bp, bi) => <div key={bi} className="flex gap-4 text-sm text-slate-800 leading-relaxed" data-id="element-1867" data-mp="15pj0hadp961f" data-mpi={bi}>
                            <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full mt-2" style={{
                  backgroundColor: '#4A2D8F'
                }} data-id="element-1868" data-mp="wqnxe75ycc2c"></span>
                            <p data-id="element-1869" data-mp="i2knimosgg89">
                              <strong className="text-slate-900" data-id="element-1870" data-mp="hzotvajz4dpm">
                                {bp.term}
                              </strong>{' '}
                              {bp.description}
                            </p>
                          </div>)}
                      </div>}
                </div> : currentPage?.type === 'question' ? <div className="max-w-2xl mx-auto space-y-6 bg-white p-8 rounded-xl border border-slate-200 shadow-sm" data-id="element-1871" data-mp="2863bhbcvv6sl">
                  <div className="flex items-start gap-3" data-id="element-1872" data-mp="1ufhtdov970vr">
                    <span className="flex-shrink-0 flex items-center justify-center w-8 h-8 rounded-full bg-purple-50 text-purple-700 font-bold" data-id="element-1873" data-mp="1ucvm7qc9yne2">
                      ?
                    </span>
                    <p className="text-base font-bold text-slate-900 leading-relaxed pt-1" data-id="element-1874" data-mp="1kimtb1jc0sfd">
                      {currentPage.question}
                    </p>
                  </div>

                  <div className="space-y-3 pl-11" data-id="element-1875" data-mp="1bhonqet8wcuo">
                    {currentPage.options.map(opt => <label key={opt.value} className={`flex items-center gap-3 cursor-pointer text-sm p-4 rounded-lg border transition-all ${selectedAnswer === opt.value ? 'bg-purple-50 border-purple-200 shadow-sm' : 'bg-white border-slate-200 hover:border-purple-300 hover:bg-slate-50'}`} data-id="element-1876" data-mp="1gh1db7bi3y0y">
                        <input type="radio" name="quiz-answer" value={opt.value} checked={selectedAnswer === opt.value} onChange={e => setSelectedAnswer(e.target.value)} className="w-4 h-4" style={{
                  accentColor: '#4A2D8F'
                }} data-id="element-1877" data-mp="3fz3b5k11ici4" />
                        <span className="font-medium text-slate-700" data-id="element-1878" data-mp="ghnag19rur4d">
                          {opt.label}
                        </span>
                      </label>)}
                  </div>

                  <div className="pl-11 h-10 flex items-center" data-id="element-1879" data-mp="2iatbvcsn22up">
                    {selectedAnswer && selectedAnswer !== currentPage.correctAnswer && <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-50 border border-red-100 text-sm font-bold text-red-700" data-id="element-1880" data-mp="h8vfwu87z5wn">
                          <span className="flex items-center justify-center w-4 h-4 rounded-full bg-red-200 text-red-700 text-xs" data-id="element-1881" data-mp="r5zti5o01t1u">
                            ✗
                          </span>
                          Wrong Answer!
                        </div>}
                    {selectedAnswer && selectedAnswer === currentPage.correctAnswer && <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-50 border border-green-100 text-sm font-bold text-green-700" data-id="element-1882" data-mp="19whgdtm5hufa">
                          <span className="flex items-center justify-center w-4 h-4 rounded-full bg-green-200 text-green-700 text-xs" data-id="element-1883" data-mp="1eusre7o6maz3">
                            ✓
                          </span>
                          Correct Answer!
                        </div>}
                  </div>
                </div> : <div className="rounded-lg p-12 text-center border-2 border-dashed border-slate-200 bg-slate-50" data-id="element-1884" data-mp="34c6dnf63jx64">
                  <p className="text-sm text-slate-500 font-medium" data-id="element-1885" data-mp="2zeo3jlgcfbwj">
                    Module content coming soon...
                  </p>
                </div>}
            </div>

            {/* Prev / Next Navigation */}
            <div className="px-6 py-5 border-t border-slate-200 flex items-center justify-between bg-slate-50/50" data-id="element-1886" data-mp="3s4d6i4cm5m0s">
              <button disabled={isFirstPage} onClick={handlePrev} className={`
                inline-flex items-center gap-1.5 px-5 py-2.5 text-sm font-semibold border rounded-md transition-colors
                ${isFirstPage ? 'border-slate-200 text-slate-300 cursor-not-allowed bg-white' : 'border-slate-300 text-slate-700 bg-white hover:bg-slate-50 shadow-sm'}
              `} data-id="element-1887" data-mp="3ich8suefvxx3">
                <ChevronLeftIcon className="h-4 w-4" data-id="element-1888" data-mp="3edfi3r6pj1ud" />
                Prev
              </button>
              <button onClick={handleNext} className="inline-flex items-center gap-1.5 px-6 py-2.5 text-sm font-bold rounded-md transition-all shadow-sm text-white hover:opacity-90 hover:shadow" style={{
            backgroundColor: '#4A2D8F'
          }} data-id="element-1889" data-mp="3j68mgdy9k85x">
                {isLastPage ? 'Proceed To Quiz' : 'Next'}
                {!isLastPage && <ChevronRightIcon className="h-4 w-4" data-id="element-1890" data-mp="5xdszwnad77x" />}
              </button>
            </div>
          </div>
        </div>}

      {/* Wrong Answer Modal (per-module question) */}
      {showWrongAnswerModal && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowWrongAnswerModal(false)} data-id="element-1891" data-mp="3t5hg15p7vkrr">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-1892" data-mp="2hozit3dspe42">
            <div className="px-5 py-4 flex items-center justify-between" style={{
          backgroundColor: '#CC0000'
        }} data-id="element-1893" data-mp="255vr3uzkxy5k">
              <div className="flex items-center gap-2 text-white" data-id="element-1894" data-mp="2e6afj37ubtda">
                <AlertCircleIcon className="h-5 w-5" data-id="element-1895" data-mp="2e73e3gwnbckc" />
                <h3 className="text-base font-bold" data-id="element-1896" data-mp="1qg6acrf07lk6">
                  {selectedAnswer ? 'Incorrect Answer' : 'Answer Required'}
                </h3>
              </div>
              <button onClick={() => setShowWrongAnswerModal(false)} className="text-white/80 hover:text-white" data-id="element-1897" data-mp="1lfs6llkfqxry">
                <XIcon className="h-5 w-5" data-id="element-1898" data-mp="1ve7wjl7q80zv" />
              </button>
            </div>
            <div className="p-5 space-y-3" data-id="element-1899" data-mp="17p6kzp3lk9qd">
              <p className="text-sm text-slate-700" data-id="element-1900" data-mp="17nvehhironvk">
                {selectedAnswer ? 'That answer is not correct. Please review the material and select the correct answer before continuing to the next section.' : 'You must select an answer before continuing to the next section.'}
              </p>
              <div className="flex justify-end pt-1" data-id="element-1901" data-mp="3n5xhsgflfilk">
                <button onClick={() => setShowWrongAnswerModal(false)} className="px-4 py-2 text-sm font-bold text-white rounded-md hover:opacity-90" style={{
              backgroundColor: '#4A2D8F'
            }} data-id="element-1902" data-mp="3n4mgqodj8oy5">
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* Section Quiz Modal (unanswered or incorrect) */}
      {quizModal.type && <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setQuizModal({
      type: null,
      items: []
    })} data-id="element-1903" data-mp="2ui1ntjhes4sh">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-1904" data-mp="15zf93j20iu0j">
            <div className="px-5 py-4 flex items-center justify-between" style={{
          backgroundColor: '#CC0000'
        }} data-id="element-1905" data-mp="1ezc4s4mtkt53">
              <div className="flex items-center gap-2 text-white" data-id="element-1906" data-mp="1jrn71e518r0t">
                <AlertCircleIcon className="h-5 w-5" data-id="element-1907" data-mp="1oodgvjq8uqds" />
                <h3 className="text-base font-bold" data-id="element-1908" data-mp="1tngp1d1itcpz">
                  {quizModal.type === 'unanswered' ? 'Unanswered Questions' : 'Some Answers Are Incorrect'}
                </h3>
              </div>
              <button onClick={() => setQuizModal({
            type: null,
            items: []
          })} className="text-white/80 hover:text-white" data-id="element-1909" data-mp="22o4tph50y0q7">
                <XIcon className="h-5 w-5" data-id="element-1910" data-mp="1bl19n5ehxxqm" />
              </button>
            </div>
            <div className="p-5 space-y-3" data-id="element-1911" data-mp="16mpymrcgth6t">
              <p className="text-sm text-slate-700" data-id="element-1912" data-mp="16o10glmcoeva">
                {quizModal.type === 'unanswered' ? <>
                    You must answer <strong data-id="element-1913" data-mp="1fn4nu1sjvgfl">all questions</strong> before
                    submitting the section quiz.
                  </> : <>
                    A score of <strong data-id="element-1914" data-mp="1ui2d2lsejpsg">100%</strong> is required to pass this
                    section quiz. Please review the section material and correct
                    the questions marked in red below.
                  </>}
              </p>
              <div className="bg-red-50 border border-red-200 rounded-md p-3" data-id="element-1915" data-mp="1zc6phkkfsq0x">
                <p className="text-xs font-semibold text-red-800 mb-2" data-id="element-1916" data-mp="2da3kwdn5mqe0">
                  {quizModal.items.length} question
                  {quizModal.items.length === 1 ? '' : 's'}{' '}
                  {quizModal.type === 'unanswered' ? `need${quizModal.items.length === 1 ? 's' : ''} an answer` : `need${quizModal.items.length === 1 ? 's' : ''} correction`}
                  :
                </p>
                <div className="flex flex-wrap gap-1.5" data-id="element-1917" data-mp="2hz61zj0wetvo">
                  {quizModal.items.map(n => <button key={n} onClick={() => scrollToQuizQuestion(n)} className="px-2.5 py-1 text-xs font-bold rounded bg-white border border-red-300 text-red-700 hover:bg-red-100 transition-colors" data-id="element-1918" data-mp="2n0ll35jimqbf">
                      Q{n}
                    </button>)}
                </div>
                <p className="text-xs text-slate-600 mt-2" data-id="element-1919" data-mp="248amvozt3gjn">
                  Click any question number to jump to it.
                </p>
              </div>
              <div className="flex justify-end pt-1" data-id="element-1920" data-mp="1ua3azkkdjooi">
                <button onClick={() => setQuizModal({
              type: null,
              items: []
            })} className="px-4 py-2 text-sm font-bold text-white rounded-md hover:opacity-90" style={{
              backgroundColor: '#4A2D8F'
            }} data-id="element-1921" data-mp="1ubo4d6uyd1ks">
                  Got it
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}